import { ref, computed, onScopeDispose, type Ref } from "vue";
import {
  getProofOfPlayMetrics,
  type ProofOfPlayMetricsResponse,
  type ProofOfPlayTimeRangePreset,
} from "@/helpers/api/metrics";
import useUserStore from "@/stores/user";

// The 1-minute range refreshes every minute; everything else every 5 minutes.
const STALE_MS_1MIN = 60_000;
const STALE_MS_DEFAULT = 300_000;

function getStaleThresholdMs(preset: ProofOfPlayTimeRangePreset): number {
  return preset === "1min" ? STALE_MS_1MIN : STALE_MS_DEFAULT;
}

interface FetchParams {
  timeRange: ProofOfPlayTimeRangePreset;
  playerUuids: string[];
  mediaIds: string[];
}

export function useReportData(
  timeRange: Ref<ProofOfPlayTimeRangePreset>,
  selectedPlayerUuids: Ref<string[]>,
  selectedMediaIds: Ref<string[]>
) {
  const user = useUserStore();

  const proofOfPlayResponse = ref<ProofOfPlayMetricsResponse | null>(null);
  const isLoading = ref(false);
  const error = ref("");
  // Incremented on every fetchData call; responses whose id no longer matches are discarded
  let latestRequestId = 0;

  // Infinite scroll pagination
  const PAGE_SIZE = 10;
  const mediaServerOffset = ref(0);
  const playerServerOffset = ref(0);
  const mediaTotalCount = ref(0);
  const playerTotalCount = ref(0);
  const isLoadingMoreMedia = ref(false);
  const isLoadingMorePlayers = ref(false);

  const hasMoreMedia = computed(() =>
    proofOfPlayResponse.value
      ? proofOfPlayResponse.value.byMedia.length < mediaTotalCount.value
      : false
  );
  const hasMorePlayers = computed(() =>
    proofOfPlayResponse.value
      ? proofOfPlayResponse.value.byPlayer.length < playerTotalCount.value
      : false
  );

  // Track last-applied params so we know when the UI is "dirty"
  const lastFetchParams = ref<FetchParams>({
    timeRange: "today",
    playerUuids: [],
    mediaIds: [],
  });
  const isDirty = computed(() => {
    if (!proofOfPlayResponse.value) return false;
    const last = lastFetchParams.value;
    if (timeRange.value !== last.timeRange) return true;
    const curPlayers = [...selectedPlayerUuids.value].sort().join(",");
    const lastPlayers = [...last.playerUuids].sort().join(",");
    if (curPlayers !== lastPlayers) return true;
    const curMedia = [...selectedMediaIds.value].sort().join(",");
    const lastMedia = [...last.mediaIds].sort().join(",");
    if (curMedia !== lastMedia) return true;
    return false;
  });

  // Becomes true once the last successful fetch has aged past the threshold
  // for its time range, prompting the user to click Update.
  const isStale = ref(false);
  let staleTimer: ReturnType<typeof setTimeout> | null = null;

  function scheduleStale(thresholdMs: number) {
    if (staleTimer) clearTimeout(staleTimer);
    isStale.value = false;
    staleTimer = setTimeout(() => {
      isStale.value = true;
    }, thresholdMs);
  }

  onScopeDispose(() => {
    if (staleTimer) {
      clearTimeout(staleTimer);
      staleTimer = null;
    }
  });

  async function fetchData() {
    const myRequestId = ++latestRequestId;
    isLoading.value = true;
    error.value = "";

    mediaServerOffset.value = 0;
    playerServerOffset.value = 0;

    try {
      const params: Record<string, any> = {
        timeRange: timeRange.value,
        mediaLimit: PAGE_SIZE,
        mediaOffset: 0,
        playerLimit: PAGE_SIZE,
        playerOffset: 0,
      };
      if (user.accountId) params.accountId = user.accountId;
      if (selectedPlayerUuids.value.length) params.playerUuids = selectedPlayerUuids.value.join(",");
      if (selectedMediaIds.value.length) params.mediaIds = selectedMediaIds.value.join(",");

      const response = await getProofOfPlayMetrics(params);

      // Drop stale response if a newer fetch has started since we awaited.
      if (myRequestId !== latestRequestId) return;

      proofOfPlayResponse.value = response;

      // Store pagination totals
      mediaTotalCount.value = response?.pagination?.byMedia?.total ?? response?.byMedia?.length ?? 0;
      playerTotalCount.value = response?.pagination?.byPlayer?.total ?? response?.byPlayer?.length ?? 0;

      // Snapshot the params we just used so isDirty resets
      lastFetchParams.value = {
        timeRange: timeRange.value,
        playerUuids: [...selectedPlayerUuids.value],
        mediaIds: [...selectedMediaIds.value],
      };

      scheduleStale(getStaleThresholdMs(timeRange.value));
    } catch (e: any) {
      // Only the latest request is allowed to surface errors / toggle loading.
      if (myRequestId !== latestRequestId) return;
      error.value = e?.message || "Failed to load report data";
      console.error("[ReportsView] fetch error:", e);
    } finally {
      if (myRequestId === latestRequestId) {
        isLoading.value = false;
      }
    }
  }

  // Build the current filter params (shared between loadMore and fetchData)
  function buildCurrentFilterParams(): Record<string, any> {
    const params: Record<string, any> = { timeRange: lastFetchParams.value.timeRange };
    if (user.accountId) params.accountId = user.accountId;
    const lp = lastFetchParams.value;
    if (lp.playerUuids.length) params.playerUuids = lp.playerUuids.join(",");
    if (lp.mediaIds.length) params.mediaIds = lp.mediaIds.join(",");
    return params;
  }

  async function loadMoreMedia() {
    if (!hasMoreMedia.value || isLoadingMoreMedia.value || !proofOfPlayResponse.value) return;
    isLoadingMoreMedia.value = true;
    // Compute the next offset as a local; only commit it after a successful
    // response. Otherwise a failed request would leave the cursor advanced
    // and the user would silently skip a page on the next click.
    const nextOffset = mediaServerOffset.value + PAGE_SIZE;
    try {
      const params = buildCurrentFilterParams();
      params.mediaLimit = PAGE_SIZE;
      params.mediaOffset = nextOffset;
      const resp = await getProofOfPlayMetrics(params);

      mediaServerOffset.value = nextOffset;

      if (resp.byMedia.length) {
        const existingIds = new Set(proofOfPlayResponse.value.byMedia.map((i) => i.media_id));
        const uniqueNew = resp.byMedia.filter((item) => !existingIds.has(item.media_id));
        proofOfPlayResponse.value.byMedia.push(...uniqueNew);
      }

      if (!resp.byMedia.length || resp.byMedia.length < PAGE_SIZE) {
        mediaTotalCount.value = proofOfPlayResponse.value.byMedia.length;
      }
    } catch (e: any) {
      console.error("[ReportsView] loadMoreMedia error:", e);
    } finally {
      isLoadingMoreMedia.value = false;
    }
  }

  async function loadMorePlayers() {
    if (!hasMorePlayers.value || isLoadingMorePlayers.value || !proofOfPlayResponse.value) return;
    isLoadingMorePlayers.value = true;
    // See loadMoreMedia: commit the offset only after a successful response.
    const nextOffset = playerServerOffset.value + PAGE_SIZE;
    try {
      const params = buildCurrentFilterParams();
      params.playerLimit = PAGE_SIZE;
      params.playerOffset = nextOffset;
      const resp = await getProofOfPlayMetrics(params);

      playerServerOffset.value = nextOffset;

      if (resp.byPlayer.length) {
        const existingIds = new Set(proofOfPlayResponse.value.byPlayer.map((i) => i.player_uuid));
        const uniqueNew = resp.byPlayer.filter((item) => !existingIds.has(item.player_uuid));
        proofOfPlayResponse.value.byPlayer.push(...uniqueNew);
      }

      if (!resp.byPlayer.length || resp.byPlayer.length < PAGE_SIZE) {
        playerTotalCount.value = proofOfPlayResponse.value.byPlayer.length;
      }
    } catch (e: any) {
      console.error("[ReportsView] loadMorePlayers error:", e);
    } finally {
      isLoadingMorePlayers.value = false;
    }
  }

  return {
    proofOfPlayResponse,
    isLoading,
    error,
    mediaTotalCount,
    playerTotalCount,
    isLoadingMoreMedia,
    isLoadingMorePlayers,
    hasMoreMedia,
    hasMorePlayers,
    isDirty,
    isStale,
    fetchData,
    buildCurrentFilterParams,
    loadMoreMedia,
    loadMorePlayers,
  };
}
